import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/feature/DashboardLayout';
import RadarChart from '../../components/base/RadarChart';
import { useAuth, api } from '../../lib/api';
import { useFamilyData } from '../../lib/context/FamilyDataContext';
import type {
  FamilyAssessment as FamilyAssessmentType,
  ChildAssessment as ChildAssessmentType,
  ChildInfo,
  PointRecordInfo,
  HabitDetailItem,
  WeekTrendItem,
  TopPunishRuleItem
} from '../../lib/api/types';

const habitLabels: Record<string, string> = {
  timeManagement: '时间管理',
  studyFocus: '学习专注',
  lifeCare: '生活自理',
  sportsHealth: '运动健康',
  responsibility: '责任担当',
  etiquette: '言行礼仪'
};

const habitConfig: Record<string, { icon: string; color: string; bg: string }> = {
  timeManagement: { icon: 'ri-time-fill', color: 'text-blue-600', bg: 'from-blue-400 to-blue-600' },
  studyFocus: { icon: 'ri-book-3-fill', color: 'text-violet-600', bg: 'from-violet-400 to-violet-600' },
  lifeCare: { icon: 'ri-home-4-fill', color: 'text-emerald-600', bg: 'from-emerald-400 to-emerald-600' },
  sportsHealth: { icon: 'ri-run-fill', color: 'text-amber-600', bg: 'from-amber-400 to-orange-500' },
  responsibility: { icon: 'ri-shield-star-fill', color: 'text-rose-600', bg: 'from-rose-400 to-rose-600' },
  etiquette: { icon: 'ri-shake-hands-fill', color: 'text-teal-600', bg: 'from-teal-400 to-teal-600' }
};

const channelConfig: Record<string, { bg: string; text: string; icon: string }> = {
  'AI': { bg: 'bg-violet-100', text: 'text-violet-700', icon: 'ri-robot-2-fill' },
  'H5': { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-smartphone-line' },
  'PC': { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'ri-computer-line' },
  '小程序': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-wechat-fill' }
};

const typeConfig: Record<number, { bg: string; text: string; label: string; dotColor: string }> = {
  1: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '奖励', dotColor: 'bg-emerald-400' },
  2: { bg: 'bg-red-100', text: 'text-red-600', label: '惩罚', dotColor: 'bg-red-400' },
  3: { bg: 'bg-amber-100', text: 'text-amber-700', label: '兑换', dotColor: 'bg-amber-400' }
};

function getScoreLabel(val: number): { label: string; bg: string; text: string } {
  if (val >= 100) return { label: '完美', bg: 'bg-amber-100', text: 'text-amber-700' };
  if (val >= 90) return { label: '优秀', bg: 'bg-emerald-100', text: 'text-emerald-700' };
  if (val >= 80) return { label: '良好', bg: 'bg-sky-100', text: 'text-sky-700' };
  if (val >= 60) return { label: '正常', bg: 'bg-blue-50', text: 'text-blue-600' };
  return { label: '待改善', bg: 'bg-red-100', text: 'text-red-600' };
}

// 7-day grouped bar + line chart
function WeekChart({ earned, spent, net, days }: { earned: number[]; spent: number[]; net: number[]; days: string[] }) {
  const W = 560, H = 180;
  const padL = 36, padR = 10, padT = 20, padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = days.length;
  const groupW = innerW / n;
  const barW = 14;
  const maxVal = Math.max(...earned, Math.abs(Math.min(...spent)), 1);
  const zeroY = padT + innerH * 0.6; // zero line at 60% from top

  const toY = (v: number) => zeroY - (v / maxVal) * (innerH * 0.6);
  const toNegY = (v: number) => zeroY - (v / maxVal) * (innerH * 0.4);

  const netPoints = net.map((v, i) => {
    const cx = padL + i * groupW + groupW / 2;
    const cy = v >= 0 ? toY(v) : toNegY(v);
    return { cx, cy, v };
  });
  const linePath = netPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.cx},${p.cy}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 180 }}>
      {/* Grid */}
      {[0, 0.3, 0.6, 1].map((r, i) => (
        <line key={i} x1={padL} y1={padT + r * innerH} x2={W - padR} y2={padT + r * innerH}
          stroke="#DBEAFE" strokeWidth="0.8" strokeDasharray={r === 0.6 ? '0' : '3,3'} />
      ))}
      {/* Zero line */}
      <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#93C5FD" strokeWidth="1.5" />
      <text x={padL - 4} y={zeroY + 3} textAnchor="end" fontSize="8" fill="#93C5FD">0</text>

      {days.map((day, i) => {
        const cx = padL + i * groupW + groupW / 2;
        const earnH = Math.max(3, (earned[i] / maxVal) * (innerH * 0.6));
        const spentH = Math.max(3, (Math.abs(spent[i]) / maxVal) * (innerH * 0.4));
        return (
          <g key={i}>
            {/* Earned bar (above zero) */}
            <rect x={cx - barW - 1} y={zeroY - earnH} width={barW} height={earnH} rx="3" fill="#34D399" opacity="0.85" />
            <text x={cx - barW / 2 - 1} y={zeroY - earnH - 3} textAnchor="middle" fontSize="7" fill="#059669" fontWeight="bold">+{earned[i]}</text>
            {/* Spent bar (below zero) */}
            <rect x={cx + 1} y={zeroY} width={barW} height={spentH} rx="3" fill="#FB7185" opacity="0.85" />
            <text x={cx + barW / 2 + 1} y={zeroY + spentH + 9} textAnchor="middle" fontSize="7" fill="#E11D48" fontWeight="bold">{spent[i]}</text>
            {/* X label */}
            <text x={cx} y={H - 4} textAnchor="middle" fontSize="9" fill="#6B7280">{day}</text>
          </g>
        );
      })}

      {/* Net line */}
      <path d={linePath} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {netPoints.map((p, i) => (
        <g key={i}>
          <circle cx={p.cx} cy={p.cy} r="3.5" fill={p.v >= 0 ? '#3B82F6' : '#EF4444'} stroke="white" strokeWidth="1.5" />
        </g>
      ))}
    </svg>
  );
}

export default function AnalyticsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const childParam = searchParams.get('child');
  const [view, setView] = useState<'family' | string>(childParam || 'family');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { currentFamily } = useAuth();
  const { children: childrenList } = useFamilyData(); // 从共享Context获取

  // 数据状态 - 预加载所有数据
  const [familyData, setFamilyData] = useState<FamilyAssessmentType | null>(null);
  const [allChildData, setAllChildData] = useState<Record<string, ChildAssessmentType>>({});
  const [allChildRecords, setAllChildRecords] = useState<Record<string, PointRecordInfo[]>>({});

  useEffect(() => {
    if (childParam) setView(childParam);
  }, [childParam]);

  // 页面挂载时预加载所有数据
  useEffect(() => {
    if (!currentFamily?.id || childrenList.length === 0) return;

    let cancelled = false;

    const loadAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. 加载家庭评估
        const familyPromise = api.assessment.getFamilyAssessment(currentFamily.id).catch(err => {
          console.error('Failed to load family assessment:', err);
          return null;
        });

        // 2. 并行加载所有儿童的评估数据
        const childPromises = childrenList.map(child =>
          api.assessment.getChildAssessment(child.id).catch(err => {
            console.error(`Failed to load child ${child.id} assessment:`, err);
            return null;
          })
        );

        // 3. 并行加载所有儿童的积分记录
        const recordPromises = childrenList.map(child =>
          api.records.getPointRecords(currentFamily.id, { child_id: child.id, page_size: 20 }).catch(err => {
            console.error(`Failed to load child ${child.id} records:`, err);
            return null;
          })
        );

        const [familyRes, ...childResults] = await Promise.all([familyPromise, ...childPromises]);
        const recordResults = await Promise.all(recordPromises);

        if (cancelled) return;

        // 设置家庭数据
        if (familyRes && familyRes.code === 0) {
          setFamilyData(familyRes.data);
        }

        // 设置所有儿童数据
        const childDataMap: Record<string, ChildAssessmentType> = {};
        const childRecordsMap: Record<string, PointRecordInfo[]> = {};
        childrenList.forEach((child, idx) => {
          const assessmentRes = childResults[idx];
          const recordsRes = recordResults[idx];
          if (assessmentRes && assessmentRes.code === 0) {
            childDataMap[child.id] = assessmentRes.data;
          }
          if (recordsRes && recordsRes.code === 0) {
            childRecordsMap[child.id] = recordsRes.data || [];
          }
        });
        setAllChildData(childDataMap);
        setAllChildRecords(childRecordsMap);

      } catch (err) {
        console.error('Failed to load analytics data:', err);
        if (!cancelled) setError('加载数据失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAllData();
    return () => { cancelled = true; };
  }, [currentFamily?.id, childrenList.length]);

  // 生成默认头像URL
  const getAvatarUrl = (child: ChildInfo | ChildAssessmentType) => {
    if ('avatar_url' in child && child.avatar_url) return child.avatar_url;
    const name = ('nickname' in child ? child.nickname : child.name) || '孩子';
    const color = ('avatar_color' in child ? child.avatar_color : '#3B82F6') || '#3B82F6';
    const bgColor = color.replace('#', '');
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${bgColor}&color=fff`;
  };

  // 获取当前视图的儿童信息
  const currentChildInfo = useMemo(() => {
    if (view === 'family') return null;
    return childrenList.find(c => c.id === view);
  }, [view, childrenList]);

  // 当前选中儿童的缓存数据
  const childData = useMemo(() => {
    if (view === 'family') return null;
    return allChildData[view] || null;
  }, [view, allChildData]);

  const childRecords = useMemo(() => {
    if (view === 'family') return [];
    return allChildRecords[view] || [];
  }, [view, allChildRecords]);

  // 获取显示的得分数据
  const displayScores = useMemo(() => {
    if (view === 'family') {
      if (!familyData?.habit_details) return {} as Record<string, number>;
      const scores: Record<string, number> = {};
      Object.entries(familyData.habit_details).forEach(([key, detail]) => {
        scores[key] = Math.round(detail.score);
      });
      return scores;
    } else {
      if (!childData?.habit_breakdown) return {} as Record<string, number>;
      const scores: Record<string, number> = {};
      Object.entries(childData.habit_breakdown).forEach(([key, score]) => {
        scores[key] = Math.round(score);
      });
      return scores;
    }
  }, [view, familyData, childData]);

  // 计算平均得分 - 家庭视图使用后端返回的habit_score，儿童视图使用前端计算
  const avgScore = useMemo(() => {
    if (view === 'family' && familyData?.habit_score) {
      // 家庭视图：直接使用后端返回的综合得分
      return Math.round(familyData.habit_score);
    }
    // 儿童视图：使用前端计算的习惯维度平均值
    const scores = Object.values(displayScores);
    if (scores.length === 0) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [view, familyData, displayScores]);

  // 获取习惯详情
  const habitDetails = useMemo(() => {
    if (view === 'family') {
      return familyData?.habit_details || {};
    } else {
      return childData?.habit_details || {};
    }
  }, [view, familyData, childData]);

  // 获取统计数据 - 确保所有积分都是整数
  const totalPoints = useMemo(() => {
    if (view === 'family') {
      return Math.round(familyData?.children?.reduce((sum, c) => sum + (c.total_points || 0), 0) || 0);
    } else {
      return Math.round(childData?.total_points || 0);
    }
  }, [view, familyData, childData]);

  const totalEarned = useMemo(() => {
    if (view === 'family') {
      return Math.round(familyData?.total_reward || 0);
    } else {
      return Math.round(childData?.total_reward_points || 0);
    }
  }, [view, familyData, childData]);

  const totalSpent = useMemo(() => {
    if (view === 'family') {
      return Math.round(familyData?.total_punish || 0);
    } else {
      return Math.round(childData?.total_punish_points || 0);
    }
  }, [view, familyData, childData]);

  const totalRedeem = useMemo(() => {
    if (view === 'family') {
      return Math.round(familyData?.children?.reduce((sum, c) => sum + (c.total_reward || 0) * 0.4, 0) || 0);
    } else {
      return Math.round(childData?.total_exchange || 0);
    }
  }, [view, familyData, childData]);

  // 动态生成整体建议 - 根据惩罚次数较多的习惯
  const generateSuggestion = useMemo(() => {
    if (view === 'family' || !childData) {
      return '';
    }

    // 找出惩罚次数最多的习惯
    const habits = Object.entries(childData.habit_details || {})
      .filter(([_, detail]) => detail && detail.punish_count > 0)
      .sort((a, b) => (b[1]?.punish_count || 0) - (a[1]?.punish_count || 0));

    if (habits.length === 0) {
      return '太棒了！近期没有惩罚记录，继续保持积极的正向激励！';
    }

    const topHabitKey = habits[0][0];
    const habitLabelsCN: Record<string, string> = {
      timeManagement: '时间管理',
      studyFocus: '学习专注',
      lifeCare: '生活自理',
      sportsHealth: '运动健康',
      responsibility: '责任担当',
      etiquette: '文明礼仪'
    };

    const habitName = habitLabelsCN[topHabitKey] || topHabitKey;

    // 根据不同习惯给出不同建议
    const suggestions: Record<string, string> = {
      timeManagement: `根据近期数据，孩子在${habitName}方面的惩罚频率较高，建议家长设置固定作息时间表，通过小目标积累大成就。可以设置早起打卡任务，每完成一天奖励2分，同时增加亲子互动时间，共同制定时间管理计划。`,
      studyFocus: `根据近期数据，孩子在${habitName}方面需要更多关注。推荐番茄工作法，25分钟专注+5分钟休息，完成后给予小奖励。可以创造安静的学习环境，减少干扰，培养良好的学习习惯。`,
      lifeCare: `根据近期数据，孩子在${habitName}方面有提升空间。建议通过示范和鼓励的方式，让孩子逐步学会自理能力。每完成一个小任务（如整理书包、收拾房间）及时给予正向激励，增强孩子的自信心。`,
      sportsHealth: `根据近期数据，孩子在${habitName}方面可以多鼓励。建议设置每日运动小目标，如跳绳100下、跑步500米等，完成后给予奖励。家长可以和孩子一起运动，增加亲子互动的同时培养运动习惯。`,
      responsibility: `根据近期数据，孩子在${habitName}方面需要引导。可以分配一些简单的家务任务，如摆碗筷、收衣服等，完成后及时肯定。通过让孩子承担小责任，培养其责任感和成就感。`,
      etiquette: `根据近期数据，孩子在${habitName}方面需要加强。建议家长以身作则，在日常生活中示范礼貌用语和行为。当孩子表现出礼貌行为时及时表扬，帮助孩子养成良好的文明礼仪习惯。`
    };

    return suggestions[topHabitKey] || `根据近期数据，孩子在${habitName}方面需要重点关注，建议家长多采用正向激励策略，减少惩罚依赖。`;
  }, [view, childData]);

  // 跳转到AI助手并自动发起习惯分析
  const goToAiAnalysis = () => {
    const childName = currentChildInfo?.name || childData?.name || '孩子';
    const childId = view === 'family' ? null : view;
    const childGender = currentChildInfo?.gender === 1 ? '男' : currentChildInfo?.gender === 2 ? '女' : '未知';
    const childAge = currentChildInfo?.birthday
      ? Math.floor((Date.now() - new Date(currentChildInfo.birthday).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
      : null;

    // 习惯类型映射
    const habitLabelsCN: Record<string, string> = {
      timeManagement: '时间管理', studyFocus: '学习专注', lifeCare: '生活自理',
      sportsHealth: '运动健康', responsibility: '责任担当', etiquette: '文明礼仪'
    };
    const habitTypeMap: Record<number, string> = {
      1: '时间管理', 2: '学习专注', 3: '生活自理',
      4: '运动健康', 5: '责任担当', 6: '文明礼仪'
    };

    // 构建分析提示词
    let analysisPrompt = '';
    if (childData && childData.habit_details) {
      const habitSummary = Object.entries(childData.habit_details)
        .map(([key, detail]) => {
          const name = habitLabelsCN[key] || key;
          const score = detail?.score || 0;
          const reward = detail?.reward_count || 0;
          const punish = detail?.punish_count || 0;
          return `${name}: 得分${score}分, 奖励${reward}次, 惩罚${punish}次`;
        })
        .join('\n');

      // 近期积分记录（带习惯类型和完整时间）
      const recentRecords = childRecords.slice(0, 10).map(r => {
        const typeLabel = r.record_type === 1 ? '奖励' : r.record_type === 2 ? '惩罚' : '兑换';
        const dateTime = r.created_at ? r.created_at.replace('T', ' ').slice(0, 16) : '';
        const habitName = r.habit_type
          ? (Array.isArray(r.habit_type) ? r.habit_type : [r.habit_type])
              .map((t: number) => habitTypeMap[t] || `类型${t}`).join('+')
          : '-';
        const ruleName = r.rule_name || r.remark || '';
        return `${dateTime} ${typeLabel} ${ruleName} ${r.points > 0 ? '+' : ''}${r.points}分 [${habitName}]`;
      }).join('\n');

      analysisPrompt = `请作为儿童心理学和教育学专家，对${childName}进行深度习惯分析和个性化建议。

【儿童信息】
- 姓名: ${childName}
- 性别: ${childGender}${childAge ? `\n- 年龄: ${childAge}岁` : ''}
- 综合评分: ${avgScore}

【六维习惯得分】
${habitSummary}

【近10天积分记录】
${recentRecords || '暂无记录'}

请从以下角度进行详细分析和建议：

1. **习惯现状分析**：分析该孩子在六个维度的表现特点，找出优势和薄弱环节。

2. **重点关注习惯的奖惩机制建议**：针对得分较低或惩罚较多的习惯维度，建议设定哪些具体的奖励规则和惩罚规则来加以控制和引导。请给出具体的规则名称、积分设置和执行建议。

3. **兑换商品调动积极性**：根据该孩子的年龄和特点，建议设置哪些兑换商品（如玩具、零食、活动特权等）来调动积极性，以及合理的积分定价。

4. **年龄适配的沟通引导**：针对${childAge ? `${childAge}岁` : '该年龄段'}儿童的心理特点，说明家长应该如何通过语言沟通、情感交流来引导孩子养成好习惯，包括具体的沟通话术和场景示例。

请给出详细、实用、可操作的建议方案。`;
    } else {
      analysisPrompt = `请作为儿童心理学和教育学专家，对${childName}（${childGender}${childAge ? `，${childAge}岁` : ''}）的习惯养成情况进行分析，给出专业的正向激励建议。`;
    }

    // 跳转到AI页面，通过state传递分析请求
    navigate('/ai', {
      state: {
        autoSend: true,
        message: analysisPrompt,
        childId: childId,
        childName: childName,
      }
    });
  };

  // 获取惩罚规则
  const punishmentRules = useMemo(() => {
    if (view === 'family') return [];
    return childData?.top_punish_rules || [];
  }, [view, childData]);

  // 获取周趋势数据
  const weekTrend = useMemo(() => {
    if (view === 'family') {
      return familyData?.week_trend || [];
    } else {
      return childData?.week_trend || [];
    }
  }, [view, familyData, childData]);

  const week7Days = weekTrend.map(t => t.date);
  const week7Earned = weekTrend.map(t => t.earned);
  const week7Spent = weekTrend.map(t => t.spent);
  const week7Net = weekTrend.map(t => t.net);

  const scoreInfo = getScoreLabel(avgScore);

  // Habit score label
  const getHabitLabel = (val: number) => {
    if (val >= 90) return { label: '优秀', bg: 'bg-emerald-100', text: 'text-emerald-700' };
    if (val >= 80) return { label: '良好', bg: 'bg-sky-100', text: 'text-sky-700' };
    if (val >= 60) return { label: '正常', bg: 'bg-blue-50', text: 'text-blue-600' };
    return { label: '待改善', bg: 'bg-red-100', text: 'text-red-600' };
  };

  // 格式化时间 - 后端存储的是本地时间(UTC+8)，直接解析即可
  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = `${yesterday.getFullYear()}-${(yesterday.getMonth() + 1).toString().padStart(2, '0')}-${yesterday.getDate().toString().padStart(2, '0')}`;
    const dateStr2 = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

    if (dateStr2 === todayStr) return '今天';
    if (dateStr2 === yesterdayStr) return '昨天';
    return `${date.getMonth() + 1}-${date.getDate()}`;
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500"></i>
            <p className="text-gray-500 mt-2">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl text-red-500"></i>
            <p className="text-gray-500 mt-2">{error}</p>
            <button
              onClick={() => view === 'family' ? loadFamilyData() : loadChildData(view)}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              重试
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900">习惯分析</h1>
            <p className="text-gray-400 text-sm mt-0.5">孩子们的习惯养成数据分析</p>
          </div>
        </div>

        {/* View Switcher */}
        <div className="flex gap-1 bg-blue-50 p-1 rounded-xl w-fit mb-5 flex-wrap border border-blue-100">
          <button
            onClick={() => setView('family')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              view === 'family' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-home-heart-fill text-sm"></i>家庭整体
          </button>
          {childrenList.map(child => (
            <button
              key={child.id}
              onClick={() => setView(child.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                view === child.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <img src={getAvatarUrl(child)} alt={child.name} className="w-5 h-5 rounded-full object-cover object-top" />
              {child.name}
            </button>
          ))}
        </div>

        {/* Score Banner */}
        <div className="rounded-2xl p-4 md:p-5 mb-5 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 50%, #1E88E5 100%)' }}>
          <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/5 rounded-full pointer-events-none"></div>
          <div className="relative z-10">
            {/* Score row */}
            <div className="flex items-end gap-2 mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-white/60 text-xs md:text-sm mb-1">
                  {view === 'family' ? '家庭整体习惯得分' : `${currentChildInfo?.name || childData?.name || '孩子'}的习惯得分`}
                </p>
                <div className="flex items-end gap-2">
                  <span className="text-4xl md:text-5xl font-black text-white">{avgScore}</span>
                  <div className="mb-1 space-y-0.5">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${scoreInfo.bg} ${scoreInfo.text}`}>{scoreInfo.label}</span>
                    <p className="text-white/50 text-[10px]">满分100分</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Stats grid - 5 items, responsive */}
            <div className="grid grid-cols-5 gap-1.5">
              <div className="bg-white/10 rounded-xl px-1.5 py-2 text-center min-w-0">
                <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-white/15 mx-auto mb-1">
                  <i className="ri-star-fill text-yellow-300 text-xs"></i>
                </div>
                <div className="text-white font-black text-xs md:text-sm">{totalPoints}</div>
                <div className="text-white/50 text-[8px] md:text-[9px] whitespace-nowrap">总积分</div>
              </div>
              <div className="bg-white/10 rounded-xl px-1.5 py-2 text-center min-w-0">
                <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-white/15 mx-auto mb-1">
                  <i className="ri-flashlight-fill text-yellow-300 text-xs"></i>
                </div>
                <div className="text-white font-black text-xs md:text-sm">{totalEarned}</div>
                <div className="text-white/50 text-[8px] md:text-[9px] whitespace-nowrap">经验值</div>
              </div>
              <div className="bg-white/10 rounded-xl px-1.5 py-2 text-center min-w-0">
                <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-white/15 mx-auto mb-1">
                  <i className="ri-add-circle-fill text-emerald-300 text-xs"></i>
                </div>
                <div className="text-white font-black text-xs md:text-sm">+{totalEarned}</div>
                <div className="text-white/50 text-[8px] md:text-[9px] whitespace-nowrap">总奖励</div>
              </div>
              <div className="bg-white/10 rounded-xl px-1.5 py-2 text-center min-w-0">
                <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-white/15 mx-auto mb-1">
                  <i className="ri-indeterminate-circle-fill text-rose-300 text-xs"></i>
                </div>
                <div className="text-white font-black text-xs md:text-sm">-{totalSpent}</div>
                <div className="text-white/50 text-[8px] md:text-[9px] whitespace-nowrap">总惩罚</div>
              </div>
              <div className="bg-white/10 rounded-xl px-1.5 py-2 text-center min-w-0">
                <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-white/15 mx-auto mb-1">
                  <i className="ri-exchange-fill text-amber-300 text-xs"></i>
                </div>
                <div className="text-white font-black text-xs md:text-sm">-{totalRedeem}</div>
                <div className="text-white/50 text-[8px] md:text-[9px] whitespace-nowrap">兑换分</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Radar Chart */}
          <div className="bg-white rounded-2xl p-5 border border-blue-100">
            <h3 className="font-bold text-blue-900 text-sm mb-4 flex items-center gap-2">
              <i className="ri-radar-fill text-blue-500"></i>
              习惯养成雷达图
            </h3>
            <div className="flex justify-center">
              <RadarChart data={displayScores} labels={habitLabels} size={240} />
            </div>
          </div>

          {/* 7-day grouped bar + line chart */}
          <div className="bg-white rounded-2xl p-5 border border-blue-100">
            <h3 className="font-bold text-blue-900 text-sm mb-1 flex items-center gap-2">
              <i className="ri-bar-chart-grouped-fill text-blue-500"></i>
              近7天积分趋势
            </h3>
            <p className="text-xs text-gray-400 mb-3">分组柱状图显示获得/扣除，折线图显示净积分</p>
            <WeekChart earned={week7Earned} spent={week7Spent} net={week7Net} days={week7Days} />
            <div className="flex items-center gap-4 justify-center mt-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-emerald-400"></div>
                <span className="text-xs text-gray-500">获得积分</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-rose-400"></div>
                <span className="text-xs text-gray-500">扣除积分</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-xs text-gray-500">净积分折线</span>
              </div>
            </div>
          </div>
        </div>

        {/* Habit Progress Cards */}
        <div className="bg-white rounded-2xl p-5 mb-4 border border-blue-100">
          <h3 className="font-bold text-blue-900 text-sm mb-4 flex items-center gap-2">
            <i className="ri-seedling-fill text-emerald-500"></i>
            六大习惯完成度
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {Object.entries(displayScores).map(([key, val]) => {
              const cfg = habitConfig[key];
              const detail = habitDetails[key];
              const quality = getHabitLabel(val);
              return (
                <div key={key} className="bg-blue-50/40 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br ${cfg.bg} flex-shrink-0`}>
                      <i className={`${cfg.icon} text-white text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div className="font-bold text-blue-900 text-sm">{habitLabels[key]}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${quality.bg} ${quality.text}`}>{quality.label}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400 flex-wrap">
                        <span><i className="ri-links-line mr-0.5"></i>{detail?.rule_count || 0}条规则</span>
                        <span className="text-emerald-600"><i className="ri-add-circle-line mr-0.5"></i>奖{detail?.reward_count || 0}次</span>
                        <span className="text-rose-500"><i className="ri-indeterminate-circle-line mr-0.5"></i>罚{detail?.punish_count || 0}次</span>
                      </div>
                    </div>
                    <div className={`text-right ${cfg.color} flex-shrink-0`}>
                      <div className="font-black text-base">{val}%</div>
                    </div>
                  </div>
                  <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${cfg.bg} rounded-full transition-all`} style={{ width: `${val}%` }}></div>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-gray-400">完成度 {val}%</span>
                    <span className={`text-xs font-medium ${val >= 80 ? 'text-emerald-600' : val >= 60 ? 'text-blue-600' : 'text-rose-500'}`}>
                      {val >= 90 ? '优秀' : val >= 80 ? '良好' : val >= 60 ? '正常' : '待改善'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Improvement Suggestions - Only show in child view */}
        {view !== 'family' && (
          <div className="bg-white rounded-2xl p-5 mb-4 border border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-blue-900 text-sm flex items-center gap-2">
                <i className="ri-lightbulb-fill text-amber-500"></i>
                提高建议
                <span className="text-xs font-normal text-gray-400 ml-1">— 惩罚次数较多的习惯</span>
              </h3>
              <button
                onClick={goToAiAnalysis}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-medium cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                <i className="ri-robot-2-fill text-xs"></i>一键咨询AI专家
              </button>
            </div>
            <div className="space-y-3">
              {punishmentRules.length > 0 ? punishmentRules.map((rule, idx) => {
                const badgeColors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-amber-100 text-amber-700'];
                const titles = ['重点关注', '需要改善', '稍加注意'];
                return (
                  <div key={rule.id} className="flex items-start gap-3 p-3 bg-blue-50/60 rounded-xl border border-blue-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex-shrink-0 text-white font-black text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-bold text-blue-900">{rule.name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badgeColors[idx]}`}>{titles[idx]}</span>
                        {rule.repeat_penalty && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-500">×{rule.penalty_multiplier}倍率</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-gray-400">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-flashlight-line text-rose-400"></i>今年已触发 <strong className="text-red-500">{rule.year_trigger_count}</strong> 次
                        </span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-subtract-line text-rose-400"></i>累计扣除 <strong className="text-rose-500">{Math.round(Math.abs(rule.total_points))}</strong> 分
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <i className="ri-checkbox-circle-line text-2xl text-emerald-500 mb-2"></i>
                  <p>太棒了！近期没有需要特别关注的惩罚记录</p>
                </div>
              )}
            </div>
            {/* Overall suggestion - Dynamic */}
            <div className="mt-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-500 flex-shrink-0">
                  <i className="ri-chat-smile-2-fill text-white text-sm"></i>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-blue-800 mb-1">整体建议</p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    {generateSuggestion}
                  </p>
                  <button
                    onClick={goToAiAnalysis}
                    className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 font-medium cursor-pointer hover:text-blue-800 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-robot-2-line"></i>获取AI个性化建议 →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Family comparison table */}
        {view === 'family' && familyData?.children && (
          <div className="bg-white rounded-2xl p-5 border border-blue-100">
            <h3 className="font-bold text-blue-900 text-sm mb-4 flex items-center gap-2">
              <i className="ri-group-fill text-blue-500"></i>
              各孩子习惯对比
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-blue-50">
                    <th className="text-left py-2 pr-4 text-gray-400 font-medium text-xs">习惯维度</th>
                    {familyData.children.map(c => (
                      <th key={c.id} className="text-center py-2 px-2 text-gray-400 font-medium text-xs whitespace-nowrap">{c.name}</th>
                    ))}
                    <th className="text-center py-2 px-2 text-gray-400 font-medium text-xs">家庭均值</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(habitLabels).map(([key, label]) => {
                    const vals = familyData.children!.map(c => c.habit_details?.[key]?.score || 75);
                    const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
                    const cfg = habitConfig[key];
                    return (
                      <tr key={key} className="border-b border-blue-50">
                        <td className="py-2.5 pr-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br ${cfg.bg} flex-shrink-0`}>
                              <i className={`${cfg.icon} text-white text-xs`}></i>
                            </div>
                            <span className="text-xs text-gray-600 font-medium">{label}</span>
                          </div>
                        </td>
                        {vals.map((v, i) => (
                          <td key={i} className="py-2.5 px-2 text-center">
                            <span className={`text-sm font-bold ${v >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>{Math.round(v)}%</span>
                          </td>
                        ))}
                        <td className="py-2.5 px-2 text-center">
                          <span className={`text-sm font-bold ${avg >= 80 ? 'text-emerald-600' : 'text-blue-600'}`}>{avg}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
